import { useEffect, useState, useRef } from "react";

/**
 * Animated number counter. Skips animation on first mount and throttles
 * display updates to avoid layout thrashing / ResizeObserver feedback loops.
 */
export function AnimatedNumber({ value, prefix = "", decimals = 0, duration = 800, className = "" }) {
    const [display, setDisplay] = useState(value || 0);
    const fromRef = useRef(value || 0);
    const rafRef = useRef(null);
    const hasAnimatedRef = useRef(false);

    useEffect(() => {
        const to = value || 0;
        if (!hasAnimatedRef.current) {
            hasAnimatedRef.current = true;
            fromRef.current = to;
            setDisplay(to);
            return;
        }

        const start = performance.now();
        const from = fromRef.current;
        let lastPaint = 0;

        const animate = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            const v = from + (to - from) * eased;
            if (now - lastPaint >= 48 || t >= 1) {
                setDisplay(v);
                lastPaint = now;
            }
            if (t < 1) {
                rafRef.current = requestAnimationFrame(animate);
            } else {
                fromRef.current = to;
            }
        };

        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(rafRef.current);
    }, [value, duration]);

    const formatted = display.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });

    return (
        <span className={`inline-block min-w-[6ch] tabular-nums ${className}`}>
            {prefix}{formatted}
        </span>
    );
}
